import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Button,
  Alert,
  Stack,
  CircularProgress,
  Box,
  // ListItemButton, // Disabled for batch creation - all fragments use shared metadata
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApi } from '../hooks/useApi';
import { BatchItem, StatusMessage } from '../types';
import { validateCreateCoralRequest, formatValidationErrors } from '../utils/validation';

export interface ChildFragmentsBatchProps {
  items: BatchItem[];
  onRemove: (rfid: string) => void;
  selectedRfid?: string;
  onSelect?: (rfid: string) => void; // Optional since individual editing is disabled
  onBatchComplete?: (successCount: number, errorCount: number) => void;
  parentCoralId?: string;
  creationDisabled?: boolean;
}

const ChildFragmentsBatch: React.FC<ChildFragmentsBatchProps> = ({
  items,
  onRemove,
  selectedRfid,
  onSelect,
  onBatchComplete,
  parentCoralId,
  creationDisabled = false,
}) => {
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [warningStatus, setWarningStatus] = useState<StatusMessage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { createCoralBatch } = useApi();

  const handleBatchCreate = async () => {
    if (creationDisabled) {
      return;
    }
    if (items.length === 0) {
      setStatus({
        type: 'warning',
        msg: 'Please scan some fragments first.',
      });
      return;
    }

    // Check if all items have required metadata
    const missingMetadata = items.filter((item) => !item.metadata.speciesCode);
    if (missingMetadata.length > 0) {
      setStatus({
        type: 'error',
        msg: `${missingMetadata.length} fragment(s) missing Species Code. Please select and fill in metadata for all fragments.`,
      });
      return;
    }

    // Validate the request payload according to OpenAPI schema
    const rfidArray = items.map((item) => item.rfid);
    const firstItem = items[0]; // Use first item as template for shared metadata

    const validationPayload = {
      speciesCode: firstItem.metadata.speciesCode,
      rfid_array: rfidArray,
      cutDate: firstItem.metadata.cutDate || undefined,
      status: firstItem.metadata.condition || 'ALIVE',
    };

    const validationResult = validateCreateCoralRequest(validationPayload);
    if (!validationResult.isValid) {
      const errorMessage = formatValidationErrors(validationResult.errors);
      setStatus({
        type: 'error',
        msg: `Validation failed:\n\n${errorMessage}`,
      });
      return;
    }

    setIsCreating(true);
    setStatus(null);
    setWarningStatus(null);

    try {
      const result = await createCoralBatch(items, parentCoralId);

      const successCount = result.count || 0;
      const totalRequested = items.length;
      const errorCount = totalRequested - successCount;

      // Show success message if any corals were created
      if (successCount > 0) {
        setStatus({
          type: 'success',
          msg: `Successfully created ${successCount} coral fragment${successCount > 1 ? 's' : ''}.`,
        });

        // If some corals were skipped, show a separate warning below the success message
        if (errorCount > 0 && result.warnings && result.warnings.length > 0) {
          const warningDetails = result.warnings.map((warning) => `• ${warning}`).join('\n');
          setWarningStatus({
            type: 'warning',
            msg: `${errorCount} coral fragment${errorCount > 1 ? 's were' : ' was'} skipped:\n\n${warningDetails}`,
          });
        }
      } else {
        // No corals created - show error with details if available
        let errorMessage = 'Failed to create any coral fragments.';
        if (result.warnings && result.warnings.length > 0) {
          const warningDetails = result.warnings.map((warning) => `• ${warning}`).join('\n');
          errorMessage += `\n\nReasons:\n${warningDetails}`;
        }
        setStatus({
          type: 'error',
          msg: errorMessage,
        });
      }

      if (onBatchComplete) {
        onBatchComplete(successCount, errorCount);
      }
    } catch (error: any) {
      console.error('Failed to create coral batch:', error);

      let errorMessage = 'Failed to create coral fragments.';

      // Extract detailed error messages from the response
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for direct error message in the response
        if (errorData.errors) {
          if (typeof errorData.errors === 'string') {
            errorMessage = errorData.errors;
          } else if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.join('\n');
          }
        }

        // Add warnings if available (may contain additional validation details)
        if (
          errorData.warnings &&
          Array.isArray(errorData.warnings) &&
          errorData.warnings.length > 0
        ) {
          const warningDetails = errorData.warnings
            .map((warning: string) => `• ${warning}`)
            .join('\n');
          errorMessage += `\n\nAdditional details:\n${warningDetails}`;
        }
      } else if (error.message) {
        // Use the message from the API client if available (already processed)
        errorMessage = error.message;
      }

      // Enhance common error messages to be more user-friendly
      if (errorMessage.includes('rfid format invalid')) {
        errorMessage = errorMessage.replace(
          'rfid format invalid; expected 24 alphanumeric string',
          'Invalid RFID format: RFIDs must be exactly 24 alphanumeric characters'
        );
      }

      if (errorMessage.includes('speciesCode is required')) {
        errorMessage = errorMessage.replace(
          'speciesCode is required',
          'Species Code is required for all coral fragments'
        );
      }

      if (errorMessage.includes('cutDate format invalid')) {
        errorMessage = errorMessage.replace(
          'cutDate format invalid; expected YYYY-MM',
          'Invalid Cut Date format: use YYYY-MM (e.g., 2024-12)'
        );
      }

      if (errorMessage.includes('invalid status')) {
        errorMessage = errorMessage.replace(
          /invalid status: (.+)/,
          'Invalid status: "$1". Must be ALIVE, DECEASED, or EXPORTED'
        );
      }

      setStatus({
        type: 'error',
        msg: errorMessage,
      });

      if (onBatchComplete) {
        onBatchComplete(0, items.length);
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ height: '100%', width: '100%', minHeight: 0 }}>
      {status && (
        <Alert severity={status.type} onClose={() => setStatus(null)}>
          <Box
            component="div"
            sx={{
              whiteSpace: 'pre-line', // Preserve line breaks
              fontFamily: 'inherit',
            }}
          >
            {status.msg}
          </Box>
        </Alert>
      )}
      {warningStatus && (
        <Alert severity={warningStatus.type} onClose={() => setWarningStatus(null)}>
          <Box
            component="div"
            sx={{
              whiteSpace: 'pre-line', // Preserve line breaks
              fontFamily: 'inherit',
            }}
          >
            {warningStatus.msg}
          </Box>
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
        <List dense>
          {items.length === 0 ? (
            <ListItem>
              <ListItemText primary="No fragments scanned yet." />
            </ListItem>
          ) : (
            items.map((item) => (
              <ListItem
                key={item.rfid}
                disablePadding
                secondaryAction={
                  <Tooltip title="Remove from queue">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => onRemove(item.rfid)}
                      aria-label="remove"
                      disabled={isCreating}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                {/* Disabled individual fragment editing - all fragments use shared metadata */}
                {/* <ListItemButton
                  selected={selectedRfid === item.rfid}
                  onClick={() => onSelect(item.rfid)}
                  disabled={isCreating}
                > */}
                <ListItemText
                  primary={item.rfid}
                  secondary={
                    item.metadata.speciesCode
                      ? `${item.metadata.speciesCode} - ${item.metadata.condition}`
                      : 'No metadata'
                  }
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    color: item.metadata.speciesCode ? 'text.secondary' : 'warning.main',
                  }}
                  sx={{ pl: 2 }} // Add padding since we removed the ListItemButton
                />
                {/* </ListItemButton> */}
              </ListItem>
            ))
          )}
        </List>
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <Button
          variant="contained"
          onClick={handleBatchCreate}
          disabled={creationDisabled || isCreating || items.length === 0}
          startIcon={isCreating ? <CircularProgress size={20} /> : undefined}
          fullWidth
        >
          {isCreating
            ? 'Creating...'
            : `Create ${items.length} Fragment${items.length !== 1 ? 's' : ''}`}
        </Button>
      </Box>
    </Stack>
  );
};

export default ChildFragmentsBatch;
